import {
  Component,
  OnDestroy,
  OnInit,
  HostBinding,
  HostListener,
  ViewEncapsulation,
} from "@angular/core";
import { MediaObserver } from "@angular/flex-layout";

import * as _ from "lodash";
import { Subject, Subscription } from "rxjs";
import { takeUntil, distinctUntilChanged, map } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

import { CoreSidebarService } from "@core/components/core-sidebar/core-sidebar.service";
import { CoreConfigService } from "@core/services/config.service";
import { CoreMediaService } from "@core/services/media.service";

import { Router } from "@angular/router";
import { User } from "app/services/models/user.model";
import { NavbarService } from "./navbar.service";
import { roles } from "app/services/const/user.const";
import { CurrentService } from "app/services/current.service";
import { RoleService } from "app/services/role.service";
import { Role, UserPermissions } from "app/services/models/role.model";
import { faUserShield } from "@fortawesome/free-solid-svg-icons";

@Component({
  selector: "app-navbar",
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class NavbarComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  public horizontalMenu: boolean;
  public hiddenMenu: boolean;
  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;
  public currentUser: User;
  public currentPermissions: UserPermissions | null = null;
  public currentRole: Role | null = null;
  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;
  roles = [...roles, { index: 5, label: "Super Admin", icon: faUserShield, color: "dark" }];
  @HostBinding("class.fixed-top")
  public isFixed = false;
  @HostBinding("class.navbar-static-style-on-scroll")
  public windowScrolled = false;
  @HostListener("window:scroll", [])
  onWindowScroll() {
    if (
      (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop > 100) &&
      this.coreConfig.layout.navbar.type == "navbar-static-top" &&
      this.coreConfig.layout.type == "horizontal"
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  private _unsubscribeAll: Subject<any>;

  constructor(
    private router: Router,
    private currentService: CurrentService,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _mediaObserver: MediaObserver,
    public _translateService: TranslateService,
    private navbarService: NavbarService,
    private roleService: RoleService
  ) {
    this.subscriptions.push(
      this.currentService.currentUser.subscribe((x) => (this.currentUser = x))
    );

    // Écouter les changements de permissions avec distinctUntilChanged sur le roleId
    // pour éviter les doubles appels API
    this.subscriptions.push(
      this.currentService.currentPermissions
        .pipe(
          map((permissions) => ({
            permissions,
            roleId: permissions?.roleId || null,
          })),
          distinctUntilChanged((prev, curr) => prev.roleId === curr.roleId)
        )
        .subscribe(({ permissions, roleId }) => {
          this.currentPermissions = permissions;
          console.log("Current Permissions in Navbar:", this.currentPermissions);
          // Charger les détails du rôle uniquement si l'utilisateur est connecté et a un roleId
          if (roleId && this.currentService.currentUser.value) {
            this.loadRoleDetails(roleId);
          } else {
            this.currentRole = null;
          }
        })
    );

    this.languageOptions = {
      en: {
        title: "English",
        flag: "us",
      },
      fr: {
        title: "French",
        flag: "fr",
      },
      de: {
        title: "German",
        flag: "de",
      },
      pt: {
        title: "Portuguese",
        flag: "pt",
      },
    };

    console.log(this.currentUser);

    console.log(this.currentRole);

    this._unsubscribeAll = new Subject();
  }

  /**
   * Charge les détails du rôle (couleur, icône) depuis l'API
   */
  private loadRoleDetails(roleId: string): void {
    this.roleService.getRole(roleId).subscribe({
      next: (role) => {
        this.currentRole = role;
        console.log("Role details loaded:", role);
      },
      error: (error) => {
        console.error("Error loading role details:", error);
        this.currentRole = null;
      },
    });
  }

  openSearch(): void {
    this.navbarService.toggleSearch();
  }

  openNotification(): void {
    this.navbarService.toggleNotification();
  }

  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  setLanguage(language): void {
    this.selectedLanguage = language;
    this._translateService.use(language);
    this._coreConfigService.setConfig({ app: { appLanguage: language } }, { emitEvent: true });
  }

  toggleDarkSkin() {
    this.subscriptions.push(
      this._coreConfigService
        .getConfig()
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((config) => {
          this.currentSkin = config.layout.skin;
        })
    );
    this.prevSkin = localStorage.getItem("prevSkin");

    if (this.currentSkin === "dark") {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin ? this.prevSkin : "default" } },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem("prevSkin", this.currentSkin);
      this._coreConfigService.setConfig({ layout: { skin: "dark" } }, { emitEvent: true });
    }
  }

  logout() {
    this.currentService.currentUser.next(null);
    const currentUrl = this.router.url;
    this.router.navigate(["/authentication/login"], {
      queryParams: {
        returnUrl: currentUrl,
        tenantId: this.currentService.currentTenant?.value?.id,
      },
    });
    this.currentService.resetAll();
  }

  ngOnInit(): void {
    this.currentUser = this.currentService.currentUser.value;

    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.coreConfig = config;
      this.horizontalMenu = config.layout.type === "horizontal";
      this.hiddenMenu = config.layout.menu.hidden === true;
      this.currentSkin = config.layout.skin;

      if (this.coreConfig.layout.type === "vertical") {
        setTimeout(() => {
          if (this.coreConfig.layout.navbar.type === "fixed-top") {
            this.isFixed = true;
          }
        }, 0);
      }
    });

    if (this.coreConfig.layout.type == "horizontal") {
      this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
        const isFixedTop = this._mediaObserver.isActive("bs-gt-xl");
        if (isFixedTop) {
          this.isFixed = false;
        } else {
          this.isFixed = true;
        }
      });
    }

    this.selectedLanguage = _.find(this.languageOptions, {
      id: this._translateService.currentLang,
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();

    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
  }
}
